
import React, { useEffect, useState} from "react";
import { 
  Box, 
  Tab, 
  Chip, 
  Card,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
  CircularProgress
} from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { LoadingButton } from "@mui/lab";

const CustomTabs= ({
  phases,
  activePhaseId,
  onPhaseChange,
  onAddPatient,
  loading = false,
  phaseDetails = {},
  renderPhaseContent,
  isTabTitleOnly=false //@info this will only used in case of the title.
}) => {
  const [actualSteps] = [];
  const defaultActiveTab = React.useMemo(() => {
      return activePhaseId?.toString() || phases?.[0]?.id?.toString();
    }, [activePhaseId, phases]);
    
    const [activeTab, setActiveTab] = useState(!isTabTitleOnly? defaultActiveTab:'');
  // Transform phase data for tabs
  const transformPhasesForTabs = (phaseList) => {   
    return phaseList?.map((phase, index) => ({
      id: phase.id || index,
      title: phase.title || `Phase ${index + 1}`,
      cycle: `Phase ${index + 1}`,
      status: phase.phase_status_details?.status_code || 0,
      phase_type: phase?.phase_type_details?.name || "Trial",
      description: phase.description,
      start_date: phase.phase_start_date,
      ...phase
    })) || [];
  };

const getChipColor = (status) => {
  switch (status?.toLowerCase()) {
    case "enrollment start":
      return "error";
    case "enrollment pending":
      return "warning";
    case "enrollment completed":
      return "success";
    default:
      return "default";
  }
};

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (onPhaseChange) {
      onPhaseChange(newValue);
    }
  };

  const handleStepAction = (step) => {
    if (step.slug === 'add_patients' && onAddPatient) {
      onAddPatient();
    }
  };

  useEffect(() => {
  if (activePhaseId) {
    setActiveTab(activePhaseId.toString());
  } else if (phases?.length) {
    setActiveTab(phases[0].id.toString());
  }
}, [activePhaseId, phases]);

  const StepIconComponent = ({ completed }) => (
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        border: `2px solid ${completed ? '#4CAF50' : '#BDBDBD'}`,
        backgroundColor: completed ? '#4CAF50' : 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {completed && (
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: 'white',
          }}
        />
      )}
    </Box>
  );

  const transformedPhases = transformPhasesForTabs(phases);
const hasTabs = Array.isArray(transformedPhases) && transformedPhases.length > 0;
if (!hasTabs) {
  return null; // show nothing
}
  return (
   <Box sx={{ width: '100%', typography: 'body1' }} className="bg-white rounded-xl">
      <TabContext value={activeTab}>
        <Box>
          <Box className="bg-[#E8EFFC]">
            <TabList
              value={activeTab}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="phase tabs"
              onChange={handleTabChange}
              sx={{ marginBottom: "-1px" }}
              TabIndicatorProps={{ style: { display: "none" } }}
            >
              {transformedPhases?.map((phase) => (
                <Tab
                  key={phase.id}
                  value={String(phase.id)}
                  label={
                    <Box>
                    <div className="flex items-center gap-2">
                      <span
                        className={`flex gap-2 ${activeTab === String(phase.id)
                          ? "text-blue-600 font-semibold"
                          : "text-black"
                          }`}
                      >{!isTabTitleOnly? `${phase?.product_details?.name}: ${phase.title}` : phase.title}
                      </span>
                    </div>
                      {!isTabTitleOnly && (phase?.phase_type || phase?.phase_status_details) && (
  <Box className="flex gap-2">
    {phase?.phase_type && (
      <Chip
        size="small"
        color="success"
        label={phase.phase_type}
      />
    )}

    {phase?.phase_status_details?.status && (
      <Chip
        size="small"
        color={getChipColor(phase.phase_status_details.status)}
        label={phase.phase_status_details.status}
      />
    )}
  </Box>
)}
                          </Box>

                  }
                  style={activeTab === String(phase.id) ? {
                    borderTop: "#3571FF solid 1px",
                    borderRight: "#3571FF solid 1px",
                    borderLeft: "#3571FF solid 1px",
                    backgroundColor: "white",
                    borderBottom: "1px solid white",
                  } : { 
                    border: "transparent", 
                    borderBottom: "1px solid #3571FF",
                    backgroundColor: activeTab === String(phase.id) ? "white" : "transparent"
                  }}
                  className={`normal-case py-3 rounded-t-lg transition`}
                />
              ))}
            </TabList>
          </Box>
        </Box>
        
        {transformedPhases?.map((phase) => (
          <TabPanel 
            key={phase.id} 
            value={String(phase.id)} 
            sx={{ 
              borderTop: "1px solid rgb(53, 113, 255)",
              padding: 0
            }}
          >
            {renderPhaseContent ? renderPhaseContent(phase) : (
              <>
                {loading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {/* Progress Stepper for Phase Steps */}
                    <Card className="shadow-none bg-[#FAFAFA] ">
                      {/* <CardContent className="pr-0 relative"> */}
                        <Stepper alternativeLabel>
                          {actualSteps?.map((step, key) => (
                            <Step key={key} completed={step.status === 2}>
                              <Tooltip
                                title={step.name}
                                placement="top"
                              >
                                <StepLabel
                                  StepIconComponent={StepIconComponent}
                                  className="items-start"
                                  slotProps={{
                                    label: {
                                      sx: { marginTop: "10px!important" },
                                    },
                                  }}
                                >
                                  <div className="flex flex-col items-start">
                                    <div className="text-[10px] sm:text-[11px] text-[#ABB7C2] text-left">
                                      STEP {key + 1}
                                    </div>
                                    <div className="text-[10px] sm:text-[13px] text-[#0D0B26] text-left break-words">
                                      {step.name}
                                    </div>
                                    <div className="text-[10px] sm:text-[11px] text-[#ABB7C2] text-left">
                                      {step.completed_date ? 
                                        new Date(step.completed_date).toLocaleDateString() : 
                                        step.expected_date ? 
                                          `Expected: ${new Date(step.expected_date).toLocaleDateString()}` : 
                                          "Pending"
                                      }
                                    </div>
                                    <div className={`text-[10px] sm:text-[11px] text-left ${
                                      step.status === 2 ? "text-green-600" : 
                                      step.status === 1 ? "text-yellow-600" : 
                                      "text-gray-400"
                                    }`}>
                                      {step.status === 2 ? "Completed" : 
                                       step.status === 1 ? "In Progress" : 
                                       "PendingSADasdaSDAS"}
                                    </div>
                                    {step.actions?.map((action, i) => (
                                      <LoadingButton
                                        key={i}
                                        variant="contained"
                                        size="small"
                                        className={`mt-2 text-[9.5px] w-full sm:w-auto 
                                          ${action.color === "error" ? "bg-red-600 hover:bg-red-700" : ""}
                                          ${action.color === "success" ? "bg-green-600 hover:bg-green-700" : ""}
                                          ${action.color === "primary" ? "bg-blue-600 hover:bg-blue-700" : ""}
                                          transform transition-all duration-300 hover:-translate-y-1 hover:shadow-lg
                                        `}
                                        onClick={() => handleStepAction(step)}
                                      >
                                        {action.title}
                                      </LoadingButton>
                                    ))}
                                  </div>
                                </StepLabel>
                              </Tooltip>
                            </Step>
                          ))}
                        </Stepper>
                      {/* </CardContent> */}
                    </Card>
                    
                    {/* Render additional phase content if provided */}
                    {phaseDetails && (
                      <div>
                        {renderPhaseContent && renderPhaseContent(phaseDetails)}
                      </div>
                    )}
                  </>
                )}
                </>
            )}
          </TabPanel>
        ))}
      </TabContext>
    </Box>
  );
};

export default CustomTabs;